import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  PrimaryBlueButton, SecondaryBlueButton, Severity, Toggle
} from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";

interface GlobalBillingDefaults {
  id: number;
  creditCardOverageBehavior: string;
  creditCardAutoUpgrade: boolean;
  invoiceOverageBehavior: string;
  invoiceAutoUpgrade: boolean;
  updatedAt: string;
  updatedBy: string | null;
}

interface SiteOverage {
  siteId: number;
  billingMethod: string;
  billingInterval: string;
  currentTermStart: string | null;
  currentTermEnd: string | null;
  overageBehaviorOverride: string | null;
  invoiceOverageBehaviorOverride: string | null;
  autoUpgradeEnabled: boolean | null;
  autoUpgradeNextTierId: number | null;
  autoUpgradeConsentedAt: string | null;
  autoUpgradeConsentedBy: string | null;
  effectiveOverageBehavior: string;
  effectiveAutoUpgrade: boolean;
  globalCreditCardBehavior: string;
  globalCreditCardAutoUpgrade: boolean;
  globalInvoiceBehavior: string;
  globalInvoiceAutoUpgrade: boolean;
}

const OVERAGE_BEHAVIORS_CC = [
  { value: "warn", label: "Warn only (allow overage, send email)" },
  { value: "block", label: "Block (hard stop at limit)" },
  { value: "auto_upgrade", label: "Auto-upgrade (charge card, bump to next tier)" }
];

const OVERAGE_BEHAVIORS_INV = [
  { value: "warn", label: "Warn only (allow overage, send email)" },
  { value: "block", label: "Block (hard stop at limit)" },
  { value: "interim_invoice", label: "Interim invoice (generate invoice + notify AM)" },
  { value: "hold", label: "Hold (flag for AM review)" }
];

function BehaviorBadge({ value }: { value: string }) {
  const colors: Record<string, string> = {
    warn: "bg-yellow-100 text-yellow-800",
    block: "bg-red-100 text-red-800",
    auto_upgrade: "bg-green-100 text-green-800",
    interim_invoice: "bg-blue-100 text-blue-800",
    hold: "bg-orange-100 text-orange-800"
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[value] ?? "bg-slate-100 text-slate-700"}`}>
      {value}
    </span>
  );
}

export default function BillingOverage() {
  const api = useApi();
  const { displayToast } = useToastAlert();
  const [searchParams, setSearchParams] = useSearchParams();

  const [globals, setGlobals] = useState<GlobalBillingDefaults | null>(null);
  const [loadingGlobals, setLoadingGlobals] = useState(true);
  const [savingGlobals, setSavingGlobals] = useState(false);
  const [globalsForm, setGlobalsForm] = useState<Omit<GlobalBillingDefaults, "id" | "updatedAt" | "updatedBy">>({
    creditCardOverageBehavior: "warn",
    creditCardAutoUpgrade: false,
    invoiceOverageBehavior: "interim_invoice",
    invoiceAutoUpgrade: false
  });

  const [siteIdInput, setSiteIdInput] = useState(searchParams.get("siteId") ?? "");
  const [siteOverage, setSiteOverage] = useState<SiteOverage | null>(null);
  const [loadingSite, setLoadingSite] = useState(false);
  const [savingSite, setSavingSite] = useState(false);
  const [siteForm, setSiteForm] = useState<Partial<SiteOverage>>({});

  useEffect(() => {
    loadGlobals();
  }, []);

  async function loadGlobals() {
    setLoadingGlobals(true);
    try {
      const data: GlobalBillingDefaults = await (await api.messaging.get("BillingOverage/global-defaults")).json();
      setGlobals(data);
      setGlobalsForm({
        creditCardOverageBehavior: data.creditCardOverageBehavior,
        creditCardAutoUpgrade: data.creditCardAutoUpgrade,
        invoiceOverageBehavior: data.invoiceOverageBehavior,
        invoiceAutoUpgrade: data.invoiceAutoUpgrade
      });
    } catch {
      displayToast(Severity.Error, "Failed to load global defaults");
    } finally {
      setLoadingGlobals(false);
    }
  }

  async function saveGlobals() {
    setSavingGlobals(true);
    try {
      const resp = await api.messaging.put("BillingOverage/global-defaults", globalsForm);
      if (resp.ok) {
        displayToast(Severity.Success, "Global defaults saved");
        loadGlobals();
      } else {
        displayToast(Severity.Error, "Save failed");
      }
    } catch {
      displayToast(Severity.Error, "Save failed");
    } finally {
      setSavingGlobals(false);
    }
  }

  async function loadSite(id?: string) {
    const targetId = id ?? siteIdInput;
    if (!targetId) return;
    setLoadingSite(true);
    setSiteOverage(null);
    try {
      const resp = await api.messaging.get(`BillingOverage/site/${targetId}`);
      if (resp.ok) {
        const data: SiteOverage = await resp.json();
        setSiteOverage(data);
        setSiteForm({
          billingMethod: data.billingMethod,
          billingInterval: data.billingInterval,
          currentTermStart: data.currentTermStart ?? undefined,
          currentTermEnd: data.currentTermEnd ?? undefined,
          overageBehaviorOverride: data.overageBehaviorOverride,
          invoiceOverageBehaviorOverride: data.invoiceOverageBehaviorOverride,
          autoUpgradeEnabled: data.autoUpgradeEnabled,
          autoUpgradeNextTierId: data.autoUpgradeNextTierId
        } as any);
        setSearchParams({ siteId: targetId });
      } else {
        displayToast(Severity.Error, "Site not found");
      }
    } catch {
      displayToast(Severity.Error, "Failed to load site");
    } finally {
      setLoadingSite(false);
    }
  }

  async function saveSite() {
    if (!siteOverage) return;
    setSavingSite(true);
    try {
      const resp = await api.messaging.put(`BillingOverage/site/${siteOverage.siteId}`, siteForm);
      if (resp.ok) {
        displayToast(Severity.Success, "Site overage settings saved");
        loadSite(String(siteOverage.siteId));
      } else {
        displayToast(Severity.Error, "Save failed");
      }
    } catch {
      displayToast(Severity.Error, "Save failed");
    } finally {
      setSavingSite(false);
    }
  }

  async function resetSiteToGlobal() {
    if (!siteOverage) return;
    const confirmed = window.confirm(`Reset site ${siteOverage.siteId} to global defaults?`);
    if (!confirmed) return;
    try {
      const resp = await api.messaging.delete(`BillingOverage/site/${siteOverage.siteId}/override`);
      if (resp.ok) {
        displayToast(Severity.Success, "Reset to global defaults");
        loadSite(String(siteOverage.siteId));
      }
    } catch {
      displayToast(Severity.Error, "Reset failed");
    }
  }

  if (loadingGlobals) return <LoadingSpinner />;

  const isInvoiceSite = siteForm.billingMethod === "invoice"
    || (!siteForm.billingMethod && siteOverage?.billingMethod === "invoice");

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">

      {/* ── Global Defaults ── */}
      <section className="bg-white border rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-1">Platform-Wide Overage Defaults</h2>
        <p className="text-sm text-slate-500 mb-5">
          Applied to all sites unless overridden per-site below.
          {globals?.updatedAt && (
            <span className="ml-2 text-xs text-slate-400">
              Last updated {new Date(globals.updatedAt).toLocaleString()} by {globals.updatedBy ?? "system"}
            </span>
          )}
        </p>

        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-slate-700 border-b pb-1">Credit Card Customers</h3>
            <div>
              <label className="block text-sm font-medium mb-1">Overage Behavior</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={globalsForm.creditCardOverageBehavior}
                onChange={e => setGlobalsForm(f => ({ ...f, creditCardOverageBehavior: e.target.value }))}
              >
                {OVERAGE_BEHAVIORS_CC.map(b => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <Toggle
                toggled={globalsForm.creditCardAutoUpgrade}
                changeToggle={() => setGlobalsForm(f => ({ ...f, creditCardAutoUpgrade: !f.creditCardAutoUpgrade }))}
              />
              <label className="text-sm font-medium">Auto-upgrade enabled by default</label>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-medium text-slate-700 border-b pb-1">Invoice Customers</h3>
            <div>
              <label className="block text-sm font-medium mb-1">Overage Behavior</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={globalsForm.invoiceOverageBehavior}
                onChange={e => setGlobalsForm(f => ({ ...f, invoiceOverageBehavior: e.target.value }))}
              >
                {OVERAGE_BEHAVIORS_INV.map(b => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <Toggle
                toggled={globalsForm.invoiceAutoUpgrade}
                changeToggle={() => setGlobalsForm(f => ({ ...f, invoiceAutoUpgrade: !f.invoiceAutoUpgrade }))}
              />
              <label className="text-sm font-medium">Auto-upgrade enabled by default</label>
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end">
          <PrimaryBlueButton label={savingGlobals ? "Saving..." : "Save Global Defaults"} onClick={saveGlobals} disabled={savingGlobals} />
        </div>
      </section>

      {/* ── Per-Site Override ── */}
      <section className="bg-white border rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-1">Per-Site Override</h2>
        <p className="text-sm text-slate-500 mb-4">
          Override global defaults for a specific site. Null fields inherit from global.
        </p>

        <div className="flex gap-3 mb-6">
          <input
            type="number"
            placeholder="Site ID"
            className="border rounded px-3 py-2 text-sm w-40"
            value={siteIdInput}
            onChange={e => setSiteIdInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && loadSite()}
          />
          <PrimaryBlueButton label={loadingSite ? "Loading..." : "Load Site"} onClick={() => loadSite()} disabled={loadingSite || !siteIdInput} />
        </div>

        {siteOverage && (
          <div className="space-y-5">
            <div className="bg-slate-50 rounded p-3 text-sm">
              <div className="flex flex-wrap gap-4">
                <span><strong>Site:</strong> {siteOverage.siteId}</span>
                <span><strong>Billing method:</strong> {siteOverage.billingMethod}</span>
                <span><strong>Effective behavior:</strong> <BehaviorBadge value={siteOverage.effectiveOverageBehavior} /></span>
                <span><strong>Auto-upgrade:</strong> {siteOverage.effectiveAutoUpgrade ? "ON" : "off"}</span>
                {siteOverage.autoUpgradeConsentedAt && (
                  <span className="text-green-700">
                    ✓ Consent recorded {new Date(siteOverage.autoUpgradeConsentedAt).toLocaleDateString()}
                    {siteOverage.autoUpgradeConsentedBy && ` by ${siteOverage.autoUpgradeConsentedBy}`}
                  </span>
                )}
              </div>
              {siteOverage.billingInterval === "annual" && siteOverage.currentTermStart && (
                <div className="mt-2 text-xs text-blue-700">
                  Annual term: {new Date(siteOverage.currentTermStart).toLocaleDateString()}
                  {siteOverage.currentTermEnd && ` → ${new Date(siteOverage.currentTermEnd).toLocaleDateString()}`}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Billing Method</label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={siteForm.billingMethod ?? ""}
                  onChange={e => setSiteForm(f => ({ ...f, billingMethod: e.target.value || undefined }))}
                >
                  <option value="">Infer from BillingPreference</option>
                  <option value="creditcard">Credit Card</option>
                  <option value="invoice">Invoice</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Billing Interval</label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={(siteForm as any).billingInterval ?? "monthly"}
                  onChange={e => setSiteForm(f => ({ ...f, billingInterval: e.target.value }))}
                >
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>

              {(siteForm as any).billingInterval === "annual" && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Term Start</label>
                    <input
                      type="date"
                      className="w-full border rounded px-3 py-2 text-sm"
                      value={(siteForm as any).currentTermStart?.slice(0, 10) ?? ""}
                      onChange={e => setSiteForm(f => ({ ...f, currentTermStart: e.target.value || null }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Term End</label>
                    <input
                      type="date"
                      className="w-full border rounded px-3 py-2 text-sm"
                      value={(siteForm as any).currentTermEnd?.slice(0, 10) ?? ""}
                      onChange={e => setSiteForm(f => ({ ...f, currentTermEnd: e.target.value || null }))}
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">
                  Overage Behavior Override
                  <span className="ml-1 text-xs text-slate-400">(null = use global)</span>
                </label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={(siteForm as any).overageBehaviorOverride ?? ""}
                  onChange={e => setSiteForm(f => ({ ...f, overageBehaviorOverride: e.target.value || null }))}
                >
                  <option value="">— Using global: {isInvoiceSite ? siteOverage.globalInvoiceBehavior : siteOverage.globalCreditCardBehavior} —</option>
                  {(isInvoiceSite ? OVERAGE_BEHAVIORS_INV : OVERAGE_BEHAVIORS_CC).map(b => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                  ))}
                </select>
              </div>

              {isInvoiceSite && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Invoice Overage Override
                    <span className="ml-1 text-xs text-slate-400">(invoice customers only)</span>
                  </label>
                  <select
                    className="w-full border rounded px-3 py-2 text-sm"
                    value={(siteForm as any).invoiceOverageBehaviorOverride ?? ""}
                    onChange={e => setSiteForm(f => ({ ...f, invoiceOverageBehaviorOverride: e.target.value || null }))}
                  >
                    <option value="">— Using global: {siteOverage.globalInvoiceBehavior} —</option>
                    {OVERAGE_BEHAVIORS_INV.map(b => (
                      <option key={b.value} value={b.value}>{b.label}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">
                  Auto-Upgrade Next Tier ID
                  <span className="ml-1 text-xs text-slate-400">(PackageVersionId, null = next in sequence)</span>
                </label>
                <input
                  type="number"
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="null = auto-detect next tier"
                  value={(siteForm as any).autoUpgradeNextTierId ?? ""}
                  onChange={e => setSiteForm(f => ({ ...f, autoUpgradeNextTierId: e.target.value ? parseInt(e.target.value) : null }))}
                />
              </div>
            </div>

            {/* AUTO-UPGRADE TOGGLE — very prominent */}
            <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-blue-900">Auto-Upgrade on Overage</div>
                  <div className="text-sm text-blue-700 mt-0.5">
                    When ON: site automatically bumps to next tier and charges/invoices.
                    Requires customer consent for credit card sites.
                    {(siteForm as any).autoUpgradeEnabled === null && (
                      <span className="ml-1">
                        Currently using global default:{" "}
                        <strong>{(isInvoiceSite ? siteOverage.globalInvoiceAutoUpgrade : siteOverage.globalCreditCardAutoUpgrade) ? "ON" : "off"}</strong>
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <select
                    className="border rounded px-2 py-1 text-sm"
                    value={(siteForm as any).autoUpgradeEnabled === null || (siteForm as any).autoUpgradeEnabled === undefined
                      ? ""
                      : (siteForm as any).autoUpgradeEnabled ? "true" : "false"}
                    onChange={e => setSiteForm(f => ({
                      ...f,
                      autoUpgradeEnabled: e.target.value === "" ? null : e.target.value === "true"
                    }))}
                  >
                    <option value="">— Inherit global —</option>
                    <option value="true">ON (override)</option>
                    <option value="false">OFF (override)</option>
                  </select>
                </div>
              </div>
              {siteOverage.autoUpgradeConsentedAt && (
                <div className="mt-2 text-xs text-green-700 bg-green-50 px-3 py-1 rounded">
                  ✓ Auto-upgrade consent on file —
                  recorded {new Date(siteOverage.autoUpgradeConsentedAt).toLocaleString()}
                  {siteOverage.autoUpgradeConsentedBy && ` by ${siteOverage.autoUpgradeConsentedBy}`}
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <SecondaryBlueButton label="Reset to Global Defaults" onClick={resetSiteToGlobal} />
              <PrimaryBlueButton label={savingSite ? "Saving..." : "Save Site Override"} onClick={saveSite} disabled={savingSite} />
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
